import {
    languageServerExtensions,
    LSPClient,
    LSPPlugin,
    Workspace,
    type Transport,
    type WorkspaceFile,
} from "@codemirror/lsp-client";
import { Text } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";

import { GUEST_PROJECT } from "idfweb-build";
import type { LanguageServerEvent } from "idfweb-runtime-protocol";

import type { RuntimeLink } from "./runtime.ts";

/** What the clangd link needs from the editors. */
export interface EditorHost {
    /** Open `path` in a tab and return its view. */
    display(path: string): Promise<EditorView | null>;
    /** Read a file that has no tab, for the references panel. */
    read(path: string): Promise<string | null>;
    /** Called after a restart, so the editors take the new client's extension. */
    restarted(): void;
    /** Called when clangd exits, so the editors drop the dead client's extension. */
    exited(): void;
    log(text: string): void;
}

/**
 * C and C++ features from clangd, which runs as a guest process in the
 * runtime worker. Each connection is a MessagePort the worker answers on;
 * the worker starts clangd for the project root once the toolchain is in the
 * guest. Documents are `file:` URIs of guest paths, which clangd sees as they are.
 */
export class ClangdLink {
    readonly #runtime: RuntimeLink;
    readonly #host: EditorHost;
    #client: LSPClient | undefined;

    constructor(runtime: RuntimeLink, host: EditorHost) {
        this.#runtime = runtime;
        this.#host = host;
    }

    /** The editor extension for a C or C++ file; starts clangd with the first. */
    extension(path: string, languageId: string) {
        this.#client ??= this.#start();
        return this.#client.plugin(`file://${path}`, languageId);
    }

    /** Start again, for a new project root; nothing starts until a C or C++ editor asks. */
    restart(): void {
        this.#client?.disconnect();
        this.#client = undefined;
        this.#host.restarted();
    }

    /** Ask clangd something about an open document, such as its symbols. */
    async request<R>(method: string, params: unknown): Promise<R | null> {
        const client = this.#client;
        if (client === undefined) return null;
        await client.initializing;
        client.sync();
        return await client.request<unknown, R>(method, params);
    }

    #start(): LSPClient {
        const channel = new MessageChannel();
        this.#runtime.post({ type: "language-server", port: channel.port2 }, [channel.port2]);
        const host = this.#host;
        const client = new LSPClient({
            rootUri: `file://${GUEST_PROJECT}`,
            // clangd answers slowly while it parses a translation unit in wasm.
            timeout: 30000,
            workspace: (c) => new PageWorkspace(c, host),
            extensions: languageServerExtensions(),
        });
        client.connect(
            portTransport(channel.port1, host, () => {
                if (this.#client !== client) return;
                this.#client = undefined;
                host.exited();
            }),
        );
        return client;
    }
}

function portTransport(port: MessagePort, host: EditorHost, exited: () => void): Transport {
    const handlers = new Set<(value: string) => void>();
    port.onmessage = (event: MessageEvent<unknown>) => {
        const data = event.data as LanguageServerEvent & object;
        if ("log" in data) host.log(data.log);
        else if ("exited" in data) {
            host.log(`clangd exited with ${data.exited}\n`);
            exited();
        } else {
            const text = JSON.stringify(data);
            for (const handler of handlers) handler(text);
        }
    };
    return {
        send: (message) => port.postMessage(JSON.parse(message)),
        subscribe: (handler) => handlers.add(handler),
        unsubscribe: (handler) => handlers.delete(handler),
    };
}

class OpenFile implements WorkspaceFile {
    readonly uri: string;
    readonly languageId: string;
    version: number;
    doc: Text;
    readonly view: EditorView | null;
    constructor(uri: string, languageId: string, version: number, doc: Text, view: EditorView | null) {
        this.uri = uri;
        this.languageId = languageId;
        this.version = version;
        this.doc = doc;
        this.view = view;
    }
    getView(): EditorView | null {
        return this.view;
    }
}

/**
 * CodeMirror's default workspace, plus what a tabbed editor needs: a jump to
 * another file opens its tab, and a reference in an unopened file is read so
 * the references panel can show its line.
 */
class PageWorkspace extends Workspace {
    files: OpenFile[] = [];
    readonly #versions = new Map<string, number>();
    readonly #host: EditorHost;

    constructor(client: LSPClient, host: EditorHost) {
        super(client);
        this.#host = host;
    }

    #nextVersion(uri: string): number {
        const version = (this.#versions.get(uri) ?? -1) + 1;
        this.#versions.set(uri, version);
        return version;
    }

    syncFiles() {
        const result = [];
        for (const file of this.files) {
            const plugin = file.view && LSPPlugin.get(file.view);
            if (!plugin || plugin.unsyncedChanges.empty) continue;
            result.push({ changes: plugin.unsyncedChanges, file, prevDoc: file.doc });
            file.doc = file.view!.state.doc;
            file.version = this.#nextVersion(file.uri);
            plugin.clear();
        }
        return result;
    }

    openFile(uri: string, languageId: string, view: EditorView): void {
        if (this.getFile(uri)) return;
        const file = new OpenFile(uri, languageId, this.#nextVersion(uri), view.state.doc, view);
        this.files.push(file);
        this.client.didOpen(file);
    }

    closeFile(uri: string): void {
        if (!this.getFile(uri)) return;
        this.files = this.files.filter((file) => file.uri !== uri);
        this.client.didClose(uri);
    }

    override async requestFile(uri: string): Promise<WorkspaceFile | null> {
        const open = this.getFile(uri);
        if (open) return open;
        const text = await this.#host.read(pathOf(uri));
        return text === null ? null : new OpenFile(uri, "", 0, Text.of(text.split("\n")), null);
    }

    override async displayFile(uri: string): Promise<EditorView | null> {
        return await this.#host.display(pathOf(uri));
    }
}

export function pathOf(uri: string): string {
    return decodeURIComponent(uri.replace(/^file:\/\//, ""));
}
